import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuthStore } from '../../stores/authStore';
import api from '../../utils/api';

const AdminDashboard = () => {
  const { user } = useAuthStore();
  const isContributor = user?.role === 'contributor';
  
  const [stats, setStats] = useState({
    totalArticles: 0,
    publishedArticles: 0,
    draftArticles: 0,
    totalCategories: 0,
    // Stats contributeur
    totalSubmitted: 0,
    pendingSubmissions: 0,
    approvedSubmissions: 0,
    totalRewards: 0
  });
  const [recentArticles, setRecentArticles] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    if (isContributor) {
      loadContributorData();
    } else {
      loadStats();
      loadRecentArticles();
    }
  }, [isContributor]);

  const loadContributorData = async () => {
    try {
      const [statsRes, submissionsRes] = await Promise.all([
        api.get('/submissions/stats'),
        api.get('/submissions/my')
      ]);

      if (statsRes.data.success) {
        const apiStats = statsRes.data.stats;
        setStats({
          ...stats,
          totalSubmitted: apiStats.total || 0,
          pendingSubmissions: apiStats.pending || 0,
          approvedSubmissions: apiStats.approved || 0,
          totalRewards: parseFloat(apiStats.total_earned || 0) // Utilise total_earned qui compte seulement les reward_credited=1
        });
        console.log('📊 Stats contributeur chargées:', apiStats);
      }

      if (submissionsRes.data.success) {
        setSubmissions(submissionsRes.data.submissions.slice(0, 5));
      }
    } catch (error) {
      console.error('Erreur chargement données contributeur:', error);
    }
  };

  const loadStats = async () => {
    try {
      const articlesRes = await api.get('/articles/all');
      const categoriesRes = await api.get('/categories/all');
      
      if (articlesRes.data.success) {
        const articles = articlesRes.data.articles;
        setStats({
          totalArticles: articles.length,
          publishedArticles: articles.filter(a => a.status === 'published').length,
          draftArticles: articles.filter(a => a.status === 'draft').length,
          totalCategories: categoriesRes.data.categories?.length || 0
        });
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const loadRecentArticles = async () => {
    try {
      const response = await api.get('/articles/all');
      if (response.data.success) {
        setRecentArticles(response.data.articles.slice(0, 5));
      }
    } catch (error) {
      console.error('Erreur chargement articles récents:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
      published: 'bg-blue-500/20 text-blue-400',
      draft: 'bg-yellow-500/20 text-yellow-400'
    };
    const labels = {
      pending: 'En attente',
      approved: 'Approuvé',
      rejected: 'Refusé',
      published: 'Publié',
      draft: 'Brouillon'
    };
    return { class: badges[status] || badges.pending, label: labels[status] || status };
  };

  const statsCards = isContributor
    ? [
        { label: 'Soumissions totales', value: stats.totalSubmitted, icon: 'file-alt', color: 'blue' },
        { label: 'En attente', value: stats.pendingSubmissions, icon: 'clock', color: 'yellow' },
        { label: 'Approuvés', value: stats.approvedSubmissions, icon: 'check-circle', color: 'green' },
        { label: 'Crédits gagnés', value: `${stats.totalRewards.toFixed(2)}€`, icon: 'coins', color: 'purple' },
      ]
    : [
        { label: 'Articles totaux', value: stats.totalArticles, icon: 'newspaper', color: 'blue' },
        { label: 'Articles publiés', value: stats.publishedArticles, icon: 'check-circle', color: 'green' },
        { label: 'Brouillons', value: stats.draftArticles, icon: 'edit', color: 'yellow' },
        { label: 'Catégories', value: stats.totalCategories, icon: 'folder', color: 'purple' },
      ];

  return (
    <AdminLayout>
      <Helmet>
        <title>Dashboard Admin - Blog SunServ</title>
      </Helmet>

      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">Vue d'ensemble de votre blog</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => (
            <div key={index} className="bg-surface rounded-xl p-6 border border-gray-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center`}>
                  <i className={`fas fa-${stat.icon} text-${stat.color}-400 text-xl`}></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/admin/articles/new"
            className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl p-6 text-gray-900 hover:transform hover:scale-105 transition-all"
          >
            <i className="fas fa-plus-circle text-3xl mb-3"></i>
            <h3 className="text-xl font-bold mb-1">{isContributor ? 'Nouvelle soumission' : 'Nouvel article'}</h3>
            <p className="text-sm opacity-80">{isContributor ? 'Proposer un article' : 'Créer un nouvel article'}</p>
          </Link>

          {!isContributor && (
            <>
              <Link
                to="/admin/articles"
                className="bg-surface rounded-xl p-6 border border-yellow-400/20 text-white hover:border-yellow-400/50 transition-all"
              >
                <i className="fas fa-newspaper text-3xl mb-3 text-yellow-400"></i>
                <h3 className="text-xl font-bold mb-1">Gérer les articles</h3>
                <p className="text-sm text-gray-400">Modifier, publier ou supprimer</p>
              </Link>

              <Link
                to="/admin/categories"
                className="bg-surface rounded-xl p-6 border border-yellow-400/20 text-white hover:border-yellow-400/50 transition-all"
              >
                <i className="fas fa-folder text-3xl mb-3 text-yellow-400"></i>
                <h3 className="text-xl font-bold mb-1">Catégories</h3>
                <p className="text-sm text-gray-400">Organiser le contenu</p>
              </Link>
            </>
          )}

          <Link
            to="/admin/submissions"
            className="bg-surface rounded-xl p-6 border border-yellow-400/20 text-white hover:border-yellow-400/50 transition-all"
          >
            <i className="fas fa-tasks text-3xl mb-3 text-yellow-400"></i>
            <h3 className="text-xl font-bold mb-1">{isContributor ? 'Mes soumissions' : 'Soumissions'}</h3>
            <p className="text-sm text-gray-400">{isContributor ? 'Voir mes articles soumis' : 'Valider les contributions'}</p>
          </Link>
        </div>

        {/* Recent Articles / Submissions */}
        <div className="bg-surface rounded-xl p-6 border border-gray-700/30">
          <h2 className="text-xl font-bold text-white mb-4">
            {isContributor ? 'Mes soumissions récentes' : 'Articles récents'}
          </h2>
          <div className="space-y-3">
            {isContributor ? (
              submissions.length > 0 ? (
                submissions.map((submission) => {
                  const badge = getStatusBadge(submission.status);
                  return (
                    <div key={submission.id} className="flex items-center justify-between p-3 bg-surface/90 rounded-lg">
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{submission.title}</h4>
                        <p className="text-sm text-gray-400">
                          {submission.category_name || 'Sans catégorie'} • {new Date(submission.submitted_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {submission.status === 'published' && submission.reward_amount > 0 ? (
                          <span className="text-sm text-green-400 font-medium">
                            +{submission.reward_amount}€
                          </span>
                        ) : submission.status === 'rejected' ? (
                          <span className="text-sm text-red-400 font-medium">
                            0€
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500 font-medium">
                            0€
                          </span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
                          {badge.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-400 text-center py-4">Aucune soumission pour le moment</p>
              )
            ) : (
              recentArticles.length > 0 ? (
                recentArticles.map((article) => {
                  const badge = getStatusBadge(article.status);
                  return (
                    <div key={article.id} className="flex items-center justify-between p-3 bg-surface/90 rounded-lg">
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{article.title}</h4>
                        <p className="text-sm text-gray-400">
                          {article.category_name} • {article.author_name}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
                        {badge.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-400 text-center py-4">Aucun article pour le moment</p>
              )
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
